// Renders the matching validation messages for the current answer state.
// Validations come from validations.json; each has a parsed_rule (empty
// means always-show) and an HTML message_html template with {Q/A} tokens
// interpolated against live state.

import { useMemo } from "react";
import type { Question, Validation } from "../types";
import { useAnswers } from "../state/AnswersContext";
import { evalRule } from "../util/rule";
import { interpolateMessage } from "../util/interpolate";

type Props = {
  validations: Validation[];
  questions: Question[];
};

export default function ValidationPanel({ validations, questions }: Props) {
  const { answers } = useAnswers();

  const questionsByCode = useMemo(() => {
    const m = new Map<string, Question>();
    for (const q of questions) m.set(q.custrecord_cpqc_question_code, q);
    return m;
  }, [questions]);

  const active = useMemo(() => {
    const sorted = [...validations].sort(
      (a, b) =>
        Number(a.custrecord_cpqc_validation_seq || 0) -
        Number(b.custrecord_cpqc_validation_seq || 0)
    );
    return sorted
      .filter((v) => {
        // Skip the developer-reference "Application" validation — its body
        // is documentation about how the {APPLICATION:name} etc. tokens
        // work, not a real summary line.
        if (v.name === "Application") return false;
        // Hide the "ID Key: …" line from the customer-facing summary.
        if (v.name === "ID Key validation") return false;
        const rule = v.custrecord_cpqc_validation_parsed_rule;
        // Empty rule = always show.
        if (!rule) return true;
        return evalRule(rule, answers);
      })
      .map((v) => ({
        id: v.id,
        name: v.name,
        type: v.custrecord_cpqc_validation_type, // 1=error, 3=info, ''=neutral
        html: stripEmptyLdLines(
          interpolateMessage(
            v.custrecord_cpqc_validation_message_html,
            answers,
            questionsByCode
          )
        ),
      }));
  }, [validations, answers, questionsByCode]);

  if (active.length === 0) return null;

  const errors = active.filter((v) => v.type === "1");
  const summaryItems = active.filter((v) => v.type !== "1");
  const summaryHtml = summaryItems.map((v) => v.html).join("");

  return (
    <div className="cpq-validations">
      <header>Configuration Summary</header>
      {errors.map((v) => (
        <div
          key={v.id}
          className="cpq-validation cpq-validation-type-1"
          dangerouslySetInnerHTML={{ __html: v.html }}
        />
      ))}
      {summaryHtml && (
        <div
          className="cpq-validation cpq-validation-summary"
          dangerouslySetInnerHTML={{ __html: summaryHtml }}
        />
      )}
    </div>
  );
}

// CPQ leaves the "LD:" line in the summary template even when LD hasn't been
// computed, so it shows as a blank "LD:" row. Drop any
// <p>…<strong>LD:</strong> VALUE…</p> whose value is empty, leaving any
// sibling lines in the same message intact.
function stripEmptyLdLines(html: string): string {
  if (!html) return html;
  return html.replace(
    /<p\b[^>]*>\s*<strong>\s*LD:\s*<\/strong>([\s\S]*?)<\/p>/gi,
    (full: string, after: string) => {
      const value = after
        .replace(/<[^>]+>/g, "")
        .replace(/&nbsp;/gi, "")
        .trim();
      return value ? full : "";
    }
  );
}
